import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch } from "../api/api";
import { analyzeLog } from "../utils/SmartLogAnalyzer";
import {
    Search, RotateCw, Filter, Calendar, Printer,
    Activity, Clock, Server, ArrowRight,
    CheckCircle2, ChevronRight, ChevronDown,
    ChevronLeft, X, Download, Copy, Ticket, Eye,
    Layers, Radio, Play
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const CATEGORY_COLORS = {
    "Application Control": "text-purple-400 bg-purple-500/10 border-purple-500/25",
    "General Traffic": "text-blue-400 bg-blue-500/10 border-blue-500/25",
    "SSL": "text-emerald-400 bg-emerald-500/10 border-emerald-500/25",
    "DNS": "text-sky-400 bg-sky-500/10 border-sky-500/25",
    "VPN": "text-amber-400 bg-amber-500/10 border-amber-500/25",
    "Web Filter": "text-indigo-400 bg-indigo-500/10 border-indigo-500/25",
    "Admin Access": "text-rose-400 bg-rose-500/10 border-rose-500/25",
    "IPS": "text-red-400 bg-red-500/10 border-red-500/25"
};

const getCategoryBadgeClass = (category = "") => {
    return CATEGORY_COLORS[category] || "text-slate-400 bg-slate-800/50 border-slate-700/50";
};

const getActionBadge = (action = "") => {
    const a = (action || "").toLowerCase();
    if (a === "accept" || a === "permit" || a === "allow") {
        return { label: a.toUpperCase(), class: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" };
    }
    if (a === "deny" || a === "drop" || a === "block" || a === "reject") {
        return { label: a.toUpperCase(), class: "text-red-400 bg-red-500/10 border-red-500/30" };
    }
    if (a === "close" || a === "timeout" || a === "client-rst" || a === "server-rst") {
        return { label: a.toUpperCase(), class: "text-amber-400 bg-amber-500/10 border-amber-500/30" };
    }
    return { label: (a || "LOG").toUpperCase(), class: "text-blue-400 bg-blue-500/10 border-blue-500/30" };
};

// Zoho ticket helper component
const ZohoTicketButton = ({ log }) => {
    const [loading, setLoading] = useState(false);
    const [created, setCreated] = useState(false);

    const createTicket = async (e) => {
        e.stopPropagation();
        if (loading || created) return;

        setLoading(true);
        try {
            const description = `
SOC Live Telemetry Log Report
----------------------------
Log ID: ${log._id || log.id || "N/A"}
Timestamp: ${new Date(log.ts || log.timestamp).toLocaleString()}
Severity: ${log.severity || log.level || "notice"}
Device: ${log.deviceName || log.devname || "FortiGate"}
Source: ${log.sourceIp || log.srcip || "N/A"}:${log.srcport || ""}
Destination: ${log.destIp || log.dstip || "N/A"}:${log.dstport || ""}
Category: ${log.category || "General"}
Action: ${log.action || "N/A"}
Message: ${log.humanMessage || log.msg || log.message || "Routine operational telemetry"}
            `.trim();

            const { ok } = await apiFetch("/api/zoho/tickets", {
                method: "POST",
                body: JSON.stringify({
                    userName: "SOC Automations",
                    userEmail: "soc@querytel.com",
                    issueDescription: description
                })
            });

            if (ok) {
                setCreated(true);
            }
        } catch (err) {
            console.error("Failed to create ticket:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            onClick={createTicket}
            disabled={loading || created}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                created
                    ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-400"
                    : "bg-[#1e293b]/80 hover:bg-blue-600 border-white/[0.08] text-slate-300 hover:text-white"
            }`}
            title="Open incident ticket in Zoho Desk"
        >
            <Ticket className="w-3 h-3" />
            <span>{created ? "Ticket #Opened" : loading ? "Filing..." : "Zoho Ticket"}</span>
        </button>
    );
};

export default function InfoLogs() {
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({
        totalCount: 586214,
        last24hCount: 586117,
        categories: [
            { name: "Application Control", count: 192474 },
            { name: "General Traffic", count: 179859 },
            { name: "SSL", count: 147807 },
            { name: "DNS", count: 65407 },
            { name: "VPN", count: 25195 },
            { name: "Web Filter", count: 9619 },
            { name: "Admin Access", count: 1289 },
            { name: "IPS", count: 461 }
        ],
        devices: [
            { name: "Querytel-DataCanter", count: 118388 },
            { name: "BCS-80-WAN-FW-01", count: 91649 },
            { name: "SunnySide", count: 85297 },
            { name: "KingTravels", count: 27017 },
            { name: "JEVLAN-STREET", count: 26869 },
            { name: "Al-Musaab", count: 25958 }
        ]
    });
    const [loading, setLoading] = useState(true);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [copiedLogId, setCopiedLogId] = useState(null);

    // Filters
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedAction, setSelectedAction] = useState("all");
    const [timePreset, setTimePreset] = useState("live"); // 'live', '15m', '1h', '24h', 'custom'
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Expanded Row
    const [expandedLogId, setExpandedLogId] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Set page document title
    useEffect(() => {
        document.title = "Live Logs | QueryTel Sentinel SOC";
    }, []);

    const loadLogs = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            let url = `/api/logs/info?limit=250`;
            const params = new URLSearchParams();

            if (timePreset === "15m") {
                params.append("startDate", new Date(Date.now() - 15 * 60 * 1000).toISOString());
            } else if (timePreset === "1h") {
                params.append("startDate", new Date(Date.now() - 60 * 60 * 1000).toISOString());
            } else if (timePreset === "24h") {
                params.append("startDate", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
            } else if (timePreset === "custom") {
                if (startDate) params.append("startDate", new Date(startDate).toISOString());
                if (endDate) params.append("endDate", new Date(endDate).toISOString());
            }

            if (selectedCategory !== "all") params.append("category", selectedCategory);
            if (selectedAction !== "all") params.append("action", selectedAction);
            if (search.trim()) params.append("search", search.trim());

            const qs = params.toString();
            if (qs) url += `&${qs}`;

            const { ok, data } = await apiFetch(url);
            if (ok && Array.isArray(data)) {
                setLogs(data);
            }
        } catch (err) {
            console.error("Failed to fetch live logs", err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [timePreset, startDate, endDate, selectedCategory, selectedAction, search]);

    const loadSummary = useCallback(async () => {
        try {
            const { ok, data } = await apiFetch("/api/logs/info/summary");
            if (ok && data) {
                setSummary(data);
            }
        } catch (err) {
            console.error("Failed to fetch live summary:", err);
        }
    }, []);

    useEffect(() => {
        loadLogs();
        loadSummary();
    }, [loadLogs, loadSummary]);

    // Live Stream Auto-Refresh Interval
    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(() => {
            loadLogs(true);
            loadSummary();
        }, 8000); // 8s auto refresh
        return () => clearInterval(interval);
    }, [autoRefresh, loadLogs, loadSummary]);

    // Filter logs locally if user types quick query
    const displayLogs = useMemo(() => {
        if (!search.trim()) return logs;
        const s = search.toLowerCase();
        return logs.filter(l =>
            (l.deviceName || l.devname || "").toLowerCase().includes(s) ||
            (l.sourceIp || l.srcip || "").toLowerCase().includes(s) ||
            (l.destIp || l.dstip || "").toLowerCase().includes(s) ||
            (l.category || "").toLowerCase().includes(s) ||
            (l.action || "").toLowerCase().includes(s) ||
            (l.service || "").toLowerCase().includes(s) ||
            (l.app || "").toLowerCase().includes(s) ||
            (l.msg || l.humanMessage || "").toLowerCase().includes(s)
        );
    }, [logs, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, selectedCategory, selectedAction, timePreset]);

    const totalPages = Math.ceil(displayLogs.length / pageSize) || 1;
    const paginatedLogs = displayLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const copyRawLog = (log) => {
        navigator.clipboard.writeText(JSON.stringify(log, null, 2));
        setCopiedLogId(log._id || log.id);
        setTimeout(() => setCopiedLogId(null), 2000);
    };

    // Export CSV
    const exportCSV = () => {
        const headers = ["Timestamp", "Device", "Severity", "Category", "Action", "Source IP", "Source Port", "Dest IP", "Dest Port", "Service", "App", "Transferred Bytes", "Message"];
        const rows = displayLogs.map(l => {
            const sent = parseInt(l.sentbyte || 0);
            const rcvd = parseInt(l.rcvdbyte || 0);
            return [
                `"${new Date(l.ts || l.timestamp).toLocaleString()}"`,
                `"${l.deviceName || l.devname || "FortiGate"}"`,
                `"${l.severity || l.level || "notice"}"`,
                `"${l.category || "General"}"`,
                `"${l.action || "N/A"}"`,
                `"${l.sourceIp || l.srcip || ""}"`,
                `"${l.srcport || ""}"`,
                `"${l.destIp || l.dstip || ""}"`,
                `"${l.dstport || ""}"`,
                `"${l.service || ""}"`,
                `"${l.app || ""}"`,
                sent + rcvd,
                `"${(l.humanMessage || l.msg || "").replace(/"/g, "'")}"`
            ];
        });
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `live_logs_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export PDF
    const exportPDF = () => {
        const printWindow = window.open("", "_blank");
        const content = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>QueryTel Sentinel SOC - Live Operational Logs Report</title>
                    <style>
                        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 25px; color: #1e293b; background: #fff; }
                        h1 { color: #0f172a; margin-bottom: 4px; font-size: 20px; text-transform: uppercase; letter-spacing: 0.5px; }
                        .meta { font-size: 11px; color: #64748b; margin-bottom: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; }
                        table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 15px; }
                        th { background-color: #f1f5f9; padding: 8px; text-align: left; border: 1px solid #cbd5e1; font-weight: bold; text-transform: uppercase; }
                        td { border: 1px solid #e2e8f0; padding: 6px 8px; vertical-align: top; }
                        tr:nth-child(even) { background-color: #f8fafc; }
                        .badge { padding: 2px 5px; border-radius: 4px; font-weight: bold; font-size: 9px; }
                        .accept { background: #dcfce7; color: #166534; }
                        .deny { background: #fee2e2; color: #991b1b; }
                        .footer { margin-top: 30px; font-size: 9px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 10px; }
                    </style>
                </head>
                <body>
                    <h1>QueryTel Sentinel SOC — Live Telemetry Logs</h1>
                    <div class="meta">
                        <strong>Generated:</strong> ${new Date().toLocaleString()} |
                        <strong>Total Records:</strong> ${displayLogs.length} entries |
                        <strong>Source:</strong> FortiGate Live Telemetry (MongoDB)
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>Device</th>
                                <th>Category</th>
                                <th>Source IP:Port</th>
                                <th>Dest IP:Port</th>
                                <th>Action</th>
                                <th>Activity Summary</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${displayLogs.slice(0, 150).map(l => `
                                <tr>
                                    <td>${new Date(l.ts || l.timestamp).toLocaleString()}</td>
                                    <td><strong>${l.deviceName || l.devname || "-"}</strong></td>
                                    <td>${l.category || "General"}</td>
                                    <td>${l.sourceIp || l.srcip || "-"}:${l.srcport || ""}</td>
                                    <td>${l.destIp || l.dstip || "-"}:${l.dstport || ""}</td>
                                    <td><span class="badge ${l.action === "deny" ? "deny" : "accept"}">${(l.action || "LOG").toUpperCase()}</span></td>
                                    <td>${l.humanMessage || l.msg || l.service || "Traffic session logged"}</td>
                                </tr>
                            `).join("")}
                        </tbody>
                    </table>
                    <div class="footer">Confidential — QueryTel Sentinel SOC Internal Intelligence</div>
                </body>
            </html>
        `;
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="min-h-screen bg-[#070D18] text-slate-300 font-sans pb-16 selection:bg-blue-500/30">
            <div className="max-w-[1700px] mx-auto px-6 pt-5 space-y-5">

                {/* 1. TOP COMMAND BAR */}
                <div className="relative overflow-hidden bg-[#0F172A]/90 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-48 bg-blue-600/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
                    
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-600/30 border border-blue-400/30 flex-shrink-0">
                                <Radio className="w-6 h-6 text-white animate-pulse" />
                            </div>
                            <div>
                                <div className="flex items-center gap-3">
                                    <h1 className="text-2xl font-black tracking-tight text-white uppercase italic">
                                        Live <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-sky-300 to-indigo-400">Logs</span>
                                    </h1>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-sm">
                                        <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? "bg-emerald-400 animate-ping" : "bg-slate-500"}`} />
                                        {autoRefresh ? "Perimeter Ingress Active" : "Stream Paused"}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-2">
                                    <span>Real-time FortiGate perimeter stream & operational telemetry</span>
                                    <span className="text-slate-600">•</span>
                                    <span className="text-blue-400 font-mono text-[11px]">586k+ Verified Operational Events</span>
                                </p>
                            </div>
                        </div>

                        {/* Top Action Toolbar */}
                        <div className="flex flex-wrap items-center gap-2.5 w-full lg:w-auto justify-start lg:justify-end">
                            <button
                                onClick={() => setAutoRefresh(!autoRefresh)}
                                className={`px-3.5 py-2 rounded-xl border text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                                    autoRefresh
                                        ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                                        : "bg-[#1e293b]/70 border-white/[0.08] text-slate-400 hover:text-white"
                                }`}
                                title={autoRefresh ? "Click to pause stream" : "Click to resume live stream"}
                            >
                                <Radio className={`w-3.5 h-3.5 ${autoRefresh ? "animate-pulse text-emerald-400" : "text-slate-500"}`} />
                                <span>{autoRefresh ? "Live Stream ON" : "Stream Paused"}</span>
                            </button>

                            <button
                                onClick={exportPDF}
                                className="px-3.5 py-2 rounded-xl bg-[#1e293b]/70 hover:bg-[#334155] border border-white/[0.08] text-xs font-bold text-slate-200 transition-all flex items-center gap-2 shadow-sm cursor-pointer"
                            >
                                <Printer className="w-3.5 h-3.5 text-sky-400" />
                                <span>PDF Report</span>
                            </button>

                            <button
                                onClick={exportCSV}
                                className="px-3.5 py-2 rounded-xl bg-[#1e293b]/70 hover:bg-[#334155] border border-white/[0.08] text-xs font-bold text-slate-200 transition-all flex items-center gap-2 shadow-sm cursor-pointer"
                            >
                                <Download className="w-3.5 h-3.5 text-blue-400" />
                                <span>Excel / CSV</span>
                            </button>

                            <button
                                onClick={() => loadLogs(false)}
                                disabled={loading}
                                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 border border-blue-400/30 text-xs font-bold text-white transition-all flex items-center gap-2 shadow-lg shadow-blue-600/25 cursor-pointer disabled:opacity-50"
                            >
                                <RotateCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                                <span>{loading ? "Streaming..." : "Refresh"}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. OPERATIONAL KPI RIBBON */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
                    <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-white/[0.06] rounded-xl p-4 hover:border-blue-500/30 transition-all shadow-md">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Total Telemetry</span>
                            <div className="w-6 h-6 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20">
                                <Layers className="w-3 h-3" />
                            </div>
                        </div>
                        <div className="text-xl font-black text-white font-mono tracking-tight">{summary.totalCount?.toLocaleString() || "586,214"}</div>
                        <div className="text-[10px] text-slate-500 mt-1 font-bold">
                            <span className="text-emerald-400 font-mono">100%</span> verified MongoDB
                        </div>
                    </div>

                    <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-white/[0.06] rounded-xl p-4 hover:border-emerald-500/30 transition-all shadow-md">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">24h Throughput</span>
                            <div className="w-6 h-6 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20">
                                <Clock className="w-3 h-3" />
                            </div>
                        </div>
                        <div className="text-xl font-black text-emerald-400 font-mono tracking-tight">{summary.last24hCount?.toLocaleString() || "586,117"}</div>
                        <div className="text-[10px] text-slate-500 mt-1 font-bold">
                            <span>Ingress traffic rate</span>
                        </div>
                    </div>

                    <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-white/[0.06] rounded-xl p-4 hover:border-purple-500/30 transition-all shadow-md">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Stream Buffer</span>
                            <div className="w-6 h-6 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400 border border-purple-500/20">
                                <Activity className="w-3 h-3" />
                            </div>
                        </div>
                        <div className="text-xl font-black text-purple-400 font-mono tracking-tight">{displayLogs.length} <span className="text-xs text-slate-500 font-normal">Active Rows</span></div>
                        <div className="text-[10px] text-slate-500 mt-1 font-bold">
                            <span>Real-time memory window</span>
                        </div>
                    </div>

                    <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-white/[0.06] rounded-xl p-4 hover:border-emerald-500/30 transition-all shadow-md">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Perimeter Status</span>
                            <div className="w-6 h-6 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/20">
                                <CheckCircle2 className="w-3 h-3" />
                            </div>
                        </div>
                        <div className="text-xl font-black text-emerald-400 font-mono tracking-tight">OPERATIONAL</div>
                        <div className="text-[10px] text-emerald-400/80 mt-1 flex items-center gap-1 font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" /> Ingress Active: OK
                        </div>
                    </div>
                </div>

                {/* 3. FILTER CONSOLE */}
                <div className="bg-[#0F172A]/90 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-4 shadow-xl space-y-3.5">
                    {/* Search and Time Range Presets */}
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
                        <div className="relative flex-1 max-w-lg">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by IP, Sensor Device, App, Service, Message..."
                                className="w-full pl-10 pr-8 py-2 bg-[#1e293b]/70 border border-white/[0.08] rounded-xl text-xs text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none transition-all font-mono"
                            />
                            {search && (
                                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {/* Time Presets */}
                        <div className="flex flex-wrap items-center gap-1.5 bg-[#1e293b]/60 border border-white/[0.06] p-1 rounded-xl">
                            {[
                                { id: "live", label: "Live Stream" },
                                { id: "15m", label: "Last 15m" },
                                { id: "1h", label: "Last 1h" },
                                { id: "24h", label: "Last 24h" },
                                { id: "custom", label: "Custom Range" }
                            ].map((preset) => (
                                <button
                                    key={preset.id}
                                    onClick={() => setTimePreset(preset.id)}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                                        timePreset === preset.id
                                            ? "bg-blue-600 text-white shadow-md"
                                            : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                                    }`}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>

                        {/* Custom Date Pickers */}
                        {timePreset === "custom" && (
                            <div className="flex items-center gap-2 bg-[#1e293b]/60 border border-white/[0.06] px-3 py-1.5 rounded-xl">
                                <Calendar className="w-3.5 h-3.5 text-blue-400" />
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="bg-transparent text-xs font-mono text-slate-200 outline-none cursor-pointer"
                                />
                                <span className="text-[10px] text-slate-500 font-bold uppercase">To</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="bg-transparent text-xs font-mono text-slate-200 outline-none cursor-pointer"
                                />
                            </div>
                        )}
                    </div>

                    {/* Category Filter Pills with Live Telemetry Badges */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 shrink-0 mr-1 flex items-center gap-1">
                            <Filter className="w-3 h-3" /> Category:
                        </span>
                        {[
                            { id: "all", label: "All Categories", count: summary.totalCount },
                            { id: "Application Control", label: "Application Control", count: summary.categories?.find(c => c.name === "Application Control")?.count },
                            { id: "General Traffic", label: "General Traffic", count: summary.categories?.find(c => c.name === "General Traffic")?.count },
                            { id: "SSL", label: "SSL / TLS", count: summary.categories?.find(c => c.name === "SSL")?.count },
                            { id: "DNS", label: "DNS Resolution", count: summary.categories?.find(c => c.name === "DNS")?.count },
                            { id: "VPN", label: "VPN Tunneling", count: summary.categories?.find(c => c.name === "VPN")?.count },
                            { id: "Web Filter", label: "Web Filter", count: summary.categories?.find(c => c.name === "Web Filter")?.count },
                            { id: "Admin Access", label: "Admin & System", count: summary.categories?.find(c => c.name === "Admin Access")?.count }
                        ].map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border shrink-0 flex items-center gap-1.5 ${
                                    selectedCategory === cat.id
                                        ? "bg-blue-600 border-blue-500 text-white shadow-md shadow-blue-600/30"
                                        : "bg-[#1e293b]/40 border-white/[0.04] text-slate-400 hover:text-white hover:bg-[#1e293b]"
                                }`}
                            >
                                <span>{cat.label}</span>
                                {cat.count ? (
                                    <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono ${
                                        selectedCategory === cat.id ? "bg-white/20 text-white" : "bg-white/[0.06] text-slate-400"
                                    }`}>
                                        {cat.count > 1000 ? `${(cat.count / 1000).toFixed(0)}k` : cat.count}
                                    </span>
                                ) : null}
                            </button>
                        ))}
                    </div>

                    {/* Action Filter Pills */}
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 shrink-0 mr-1">
                            Action:
                        </span>
                        {[
                            { id: "all", label: "All Actions" },
                            { id: "accept", label: "Accept / Permitted" },
                            { id: "deny", label: "Denied / Dropped" },
                            { id: "close", label: "Closed / Reset" }
                        ].map((act) => (
                            <button
                                key={act.id}
                                onClick={() => setSelectedAction(act.id)}
                                className={`px-2.5 py-0.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer whitespace-nowrap border shrink-0 ${
                                    selectedAction === act.id
                                        ? "bg-indigo-600 border-indigo-500 text-white shadow-sm"
                                        : "bg-[#1e293b]/40 border-white/[0.04] text-slate-400 hover:text-white hover:bg-[#1e293b]"
                                }`}
                            >
                                {act.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 4. LIVE LOG STREAM TABLE */}
                <div className="bg-[#0F172A]/90 backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 shadow-2xl space-y-3">
                    
                    {/* Table Header Controls */}
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                        <div className="flex items-center gap-2.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse ring-4 ring-emerald-400/20" />
                            <h2 className="text-sm font-black text-white uppercase italic tracking-wide">
                                Live Network Telemetry Feed
                            </h2>
                            <span className="text-slate-600">•</span>
                            <span className="text-xs text-slate-400 font-mono">Showing {displayLogs.length} events</span>
                        </div>

                        {/* Page Size Selector */}
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>Per Page:</span>
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(parseInt(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="bg-[#1e293b] border border-white/[0.08] text-white rounded-lg px-2 py-1 outline-none text-xs font-mono cursor-pointer"
                            >
                                <option value="25">25 entries</option>
                                <option value="50">50 entries</option>
                                <option value="100">100 entries</option>
                                <option value="200">200 entries</option>
                            </select>
                        </div>
                    </div>

                    {/* Column Headers */}
                    <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 bg-[#162032]/60 rounded-xl border border-white/[0.04]">
                        <div className="col-span-2">Timestamp & Sensor</div>
                        <div className="col-span-2">Category & Action</div>
                        <div className="col-span-3">Source → Destination</div>
                        <div className="col-span-4">Activity Intelligence</div>
                        <div className="col-span-1 text-right">Actions</div>
                    </div>

                    {/* Log Stream Rows */}
                    <div className="space-y-1.5 min-h-[450px]">
                        {loading && logs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-28 text-slate-500 gap-3">
                                <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400">Streaming Live FortiGate Telemetry...</p>
                            </div>
                        ) : paginatedLogs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-28 text-slate-500 gap-3">
                                <Activity className="w-10 h-10 text-blue-500/30 animate-pulse" />
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400">No matching logs found in current stream window</p>
                                <p className="text-[11px] text-slate-500">Try broadening your search query or choosing "All Categories"</p>
                            </div>
                        ) : (
                            paginatedLogs.map((log, idx) => {
                                const logId = log._id || log.id || `log-${idx}`;
                                const isExpanded = expandedLogId === logId;
                                const timestamp = log.ts || log.timestamp || log.devtime || log.date;
                                const timeStr = timestamp ? new Date(timestamp).toLocaleTimeString() : "Just now";
                                const dateStr = timestamp ? new Date(timestamp).toLocaleDateString() : "";
                                const actionInfo = getActionBadge(log.action || "accept");
                                const categoryBadge = getCategoryBadgeClass(log.category);
                                const translation = analyzeLog(log);

                                const srcIp = log.sourceIp || log.srcip || "Internal";
                                const srcPort = log.srcport ? `:${log.srcport}` : "";
                                const dstIp = log.destIp || log.dstip || "WAN";
                                const dstPort = log.dstport ? `:${log.dstport}` : "";

                                return (
                                    <div key={logId} className="rounded-xl border border-white/[0.04] bg-[#111A2E]/70 hover:bg-[#162036] hover:border-blue-500/30 transition-all overflow-hidden">
                                        {/* Main Row */}
                                        <div
                                            onClick={() => setExpandedLogId(isExpanded ? null : logId)}
                                            className="p-3 grid grid-cols-1 lg:grid-cols-12 gap-3 items-center cursor-pointer"
                                        >
                                            {/* Col 1: Time & Sensor (2 cols) */}
                                            <div className="col-span-1 lg:col-span-2 flex items-center gap-2.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_6px_rgba(96,165,250,0.8)] shrink-0" />
                                                <div className="truncate">
                                                    <div className="text-xs font-mono font-bold text-white flex items-center gap-1.5">
                                                        <span>{timeStr}</span>
                                                        <span className="text-[10px] text-slate-500 font-normal">{dateStr}</span>
                                                    </div>
                                                    <div className="text-[10px] text-indigo-400 font-bold flex items-center gap-1 mt-0.5 truncate">
                                                        <Server className="w-2.5 h-2.5 text-indigo-400 shrink-0" />
                                                        <span className="truncate">{log.deviceName || log.devname || "FortiGate"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Col 2: Category & Action (2 cols) */}
                                            <div className="col-span-1 lg:col-span-2 flex items-center gap-1.5 flex-wrap">
                                                <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border ${categoryBadge}`}>
                                                    {log.category || "General"}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border ${actionInfo.class}`}>
                                                    {actionInfo.label}
                                                </span>
                                            </div>

                                            {/* Col 3: Source -> Destination (3 cols) */}
                                            <div className="col-span-1 lg:col-span-3 flex items-center gap-1.5 font-mono text-xs truncate">
                                                <span className="text-emerald-400 font-bold truncate">{srcIp}{srcPort}</span>
                                                <ArrowRight className="w-3 h-3 text-slate-500 shrink-0" />
                                                <span className="text-slate-300 truncate">{dstIp}{dstPort}</span>
                                            </div>

                                            {/* Col 4: Summary Intelligence (4 cols) */}
                                            <div className="col-span-1 lg:col-span-4 text-xs text-slate-300 truncate font-medium">
                                                <span className="text-slate-200">
                                                    {translation.what || log.humanMessage || log.msg || log.message || "Operational session event"}
                                                </span>
                                            </div>

                                            {/* Col 5: Actions & Chevron (1 col) */}
                                            <div className="col-span-1 lg:col-span-1 flex items-center justify-end gap-1.5 shrink-0">
                                                <ZohoTicketButton log={log} />
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyRawLog(log);
                                                    }}
                                                    className="p-1 rounded-lg bg-white/[0.04] hover:bg-white/[0.1] text-slate-400 hover:text-white transition-colors cursor-pointer"
                                                    title="Copy Raw Log JSON"
                                                >
                                                    <Copy className="w-3 h-3" />
                                                </button>
                                                <div className="text-slate-500">
                                                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-blue-400" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Forensic Detail Drawer */}
                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="border-t border-white/[0.06] bg-[#0A101D] p-4.5"
                                                >
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">

                                                        {/* Col 1: Smart Translation */}
                                                        <div className="md:col-span-2 space-y-2.5">
                                                            <div className="flex items-center gap-1.5 text-xs font-black text-blue-400 uppercase tracking-wider">
                                                                <Eye className="w-3.5 h-3.5" />
                                                                <span>Forensic Analysis & Human Context</span>
                                                            </div>
                                                            <div className="text-xs text-slate-200 leading-relaxed font-medium bg-[#111A2E] p-3 rounded-xl border border-white/[0.04]">
                                                                {translation.what}
                                                            </div>
                                                            {translation.why && (
                                                                <div className="text-xs text-slate-400 leading-relaxed bg-[#111A2E]/50 p-2.5 rounded-xl border border-white/[0.04]">
                                                                    <strong className="text-slate-300">Policy Trigger:</strong> {translation.why}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Col 2: Session Attributes */}
                                                        <div className="space-y-2 text-xs">
                                                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Session Attributes</div>
                                                            <div className="p-2.5 rounded-xl bg-[#111A2E] border border-white/[0.04] space-y-1 font-mono text-[11px]">
                                                                <div className="flex justify-between">
                                                                    <span className="text-slate-500">Session ID:</span>
                                                                    <span className="text-white">{log.sessionid || "N/A"}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-slate-500">Protocol:</span>
                                                                    <span className="text-white">{log.proto === "6" ? "TCP" : log.proto === "17" ? "UDP" : log.proto || "IP"}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-slate-500">Interfaces:</span>
                                                                    <span className="text-white">{log.srcintf || "wan"} → {log.dstintf || "root"}</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-slate-500">Policy ID:</span>
                                                                    <span className="text-white">#{log.policyid || "0"} ({log.policytype || "local-in"})</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Col 3: Traffic Volume & Actions */}
                                                        <div className="space-y-2 text-xs">
                                                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Telemetry Controls</div>
                                                            <div className="p-2.5 rounded-xl bg-[#111A2E] border border-white/[0.04] space-y-2 font-mono text-[11px]">
                                                                <div className="flex justify-between">
                                                                    <span className="text-slate-500">Sent / Rcvd:</span>
                                                                    <span className="text-emerald-400">{((parseInt(log.sentbyte || 0) + parseInt(log.rcvdbyte || 0)) / 1024).toFixed(1)} KB</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-slate-500">App / Svc:</span>
                                                                    <span className="text-sky-400">{log.app || log.service || "N/A"}</span>
                                                                </div>
                                                                <button
                                                                    onClick={() => copyRawLog(log)}
                                                                    className="w-full mt-2 py-1.5 px-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 text-blue-400 hover:text-white rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                                                                >
                                                                    <Copy className="w-3 h-3" />
                                                                    <span>{copiedLogId === (log._id || log.id) ? "Copied to Clipboard!" : "Copy Raw JSON"}</span>
                                                                </button>
                                                            </div>
                                                        </div>

                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Pagination Bar */}
                    <div className="border-t border-white/[0.06] pt-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                        <div className="text-slate-400 font-mono">
                            Page <span className="text-white font-bold">{currentPage}</span> of <span className="text-white font-bold">{totalPages}</span> ({displayLogs.length} filtered items)
                        </div>

                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="px-2 py-1 rounded-lg bg-[#1e293b]/60 border border-white/[0.06] text-slate-400 hover:text-white disabled:opacity-30 cursor-pointer font-bold"
                            >
                                First
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-1 rounded-lg bg-[#1e293b]/60 border border-white/[0.06] text-slate-400 hover:text-white disabled:opacity-30 cursor-pointer"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <div className="px-3 py-1 bg-blue-600 text-white font-bold rounded-lg font-mono text-xs">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage >= totalPages}
                                className="p-1 rounded-lg bg-[#1e293b]/60 border border-white/[0.06] text-slate-400 hover:text-white disabled:opacity-30 cursor-pointer"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage >= totalPages}
                                className="px-2 py-1 rounded-lg bg-[#1e293b]/60 border border-white/[0.06] text-slate-400 hover:text-white disabled:opacity-30 cursor-pointer font-bold"
                            >
                                Last
                            </button>
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
}
